/**
 * Sandbox Service
 *
 * Service for managing ADC sandbox configurations.
 * Handles CRUD operations against the CRP management API.
 *
 * API pattern:
 *   PUT/GET/DELETE automationProjects/{project}/sandboxConfigurations/{name}
 *   GET automationProjects/{project}/sandboxConfigurations (list all)
 */

import {
  type SandboxConfiguration,
  type PutSandboxConfigurationRequest,
  SandboxProvisioningState,
  RepositoryAuthType,
  parseSandboxConfigName,
} from '@/types/sandbox';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { isLocalMode } from './localRuntimeClient';
import { ApiError } from './apiError';

// =============================================================================
// TYPES
// =============================================================================

export interface SandboxServiceContext {
  subscriptionId: string;
  resourceGroup: string;
  automationProjectName: string;
  accessToken: string;
}

// =============================================================================
// API HELPERS
// =============================================================================

const API_VERSION = apiVersions.otto;
const REQUEST_TIMEOUT = 30000;

function buildSandboxDataplaneUrl(
  projectName: string,
  sandboxConfigName?: string,
): string {
  const sandboxDataplaneBaseUrl = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/sandboxConfigurations`;
  if (!sandboxConfigName) {
    return `${sandboxDataplaneBaseUrl}?api-version=${API_VERSION}`;
  }
  return `${sandboxDataplaneBaseUrl}/${encodeURIComponent(sandboxConfigName)}?api-version=${API_VERSION}`;
}

async function dataplaneRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  accessToken: string,
  body?: unknown
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (!isLocalMode()) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const options: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      throw await ApiError.fromResponse(response);
    }

    // Handle responses with no content (204 No Content, or any empty body)
    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// =============================================================================
// CRUD OPERATIONS
// =============================================================================

/**
 * Create or update a sandbox configuration.
 *
 * Every PUT triggers a build (re-clones repo, rebuilds disk image). If a build
 * is already in progress the backend returns 409 Conflict.
 */
export async function createOrUpdateSandboxConfiguration(
  accessToken: string,
  projectName: string,
  sandboxConfigName: string,
  body: PutSandboxConfigurationRequest,
): Promise<SandboxConfiguration> {
  if (isLocalMode()) {
    const idx = mockSandboxes.findIndex((s) => s.name === sandboxConfigName);
    if (idx >= 0) mockSandboxes.splice(idx, 1);
    const mockSandbox = getMockPutSandboxConfigurationResponse(projectName, sandboxConfigName, body);
    mockSandboxes.push(mockSandbox);
    return mockSandbox;
  }
  const url = buildSandboxDataplaneUrl(projectName, sandboxConfigName);
  const sandbox = await dataplaneRequest<SandboxConfiguration>('PUT', url, accessToken, body);
  sandbox.name = parseSandboxConfigName(sandbox.name);
  return sandbox;
}

/**
 * Get a single sandbox configuration by name.
 */
export async function getSandboxConfiguration(
  accessToken: string,
  projectName: string,
  sandboxConfigName: string,
): Promise<SandboxConfiguration | null> {
  if (isLocalMode()) {
    const sandbox = mockSandboxes.find((s) => s.name === sandboxConfigName);
    return sandbox ?? null;
  }
  const url = buildSandboxDataplaneUrl(projectName, sandboxConfigName);
  const sandbox = await dataplaneRequest<SandboxConfiguration | undefined>('GET', url, accessToken);
  if (!sandbox) return null;
  sandbox.name = parseSandboxConfigName(sandbox.name);
  return sandbox;
}

/**
 * List all sandbox configurations in a project.
 */
export async function listSandboxConfigurations(
  accessToken: string,
  projectName: string
): Promise<SandboxConfiguration[]> {
  if (isLocalMode()) {
    return [...mockSandboxes];
  }
  const url = buildSandboxDataplaneUrl(projectName);
  const response = await dataplaneRequest<SandboxConfiguration[]>('GET', url, accessToken);
  return response?.map((sandbox) => {
    sandbox.name = parseSandboxConfigName(sandbox.name);
    return sandbox;
  }) ?? [];
}

/**
 * Delete a sandbox configuration.
 */
export async function deleteSandboxConfiguration(
  accessToken: string,
  projectName: string,
  sandboxConfigName: string,
): Promise<void> {
  if (isLocalMode()) {
    const idx = mockSandboxes.findIndex((s) => s.name === sandboxConfigName);
    if (idx >= 0) mockSandboxes.splice(idx, 1);
    return;
  }
  const url = buildSandboxDataplaneUrl(projectName, sandboxConfigName);
  await dataplaneRequest<void>('DELETE', url, accessToken);
}

// =============================================================================
// MOCK DATA (for local development without ARM)
// =============================================================================

const mockSandboxes: SandboxConfiguration[] = [
  {
    id: '/automationProjects/mock/sandboxConfigurations/code-review-sandbox',
    name: 'code-review-sandbox',
    type: 'automationProjects/sandboxConfigurations',
    properties: {
      provisioningState: SandboxProvisioningState.Ready,
      createdTime: '2026-03-27T00:20:00Z',
      repositorySettings: {
        repositories: [
          {
            url: 'https://msazure.visualstudio.com/One/_git/EngSys-MDA-GenevaAutomation',
            branch: 'main',
            authType: RepositoryAuthType.ManagedIdentity,
          },
        ],
      },
      createdBy: {
        objectId: 'user-123',
        tenantId: 'tenant-123',
      },
    },
  },
  {
    id: '/automationProjects/mock/sandboxConfigurations/data-pipeline-sandbox',
    name: 'data-pipeline-sandbox',
    type: 'automationProjects/sandboxConfigurations',
    properties: {
      provisioningState: SandboxProvisioningState.InProgress,
      repositorySettings: {
        repositories: [
          {
            url: 'https://github.com/contoso/data-pipeline',
            branch: 'main',
            authType: RepositoryAuthType.PAT,
          },
          {
            url: 'https://github.com/contoso/data-utils',
            branch: 'develop',
            authType: RepositoryAuthType.PAT,
          },
        ],
      },
      createdBy: {
        objectId: 'user-123',
        tenantId: 'tenant-123',
      },
    },
  },
  {
    id: '/automationProjects/mock/sandboxConfigurations/ml-model-sandbox',
    name: 'ml-model-sandbox',
    type: 'automationProjects/sandboxConfigurations',
    properties: {
      provisioningState: SandboxProvisioningState.Failed,
      errorMessage: 'Failed to clone repository: authentication error',
      repositorySettings: {
        repositories: [
          {
            url: 'https://github.com/contoso/ml-models',
            branch: 'main',
            authType: RepositoryAuthType.PAT,
          },
        ],
      },
      createdBy: {
        objectId: 'user-123',
        tenantId: 'tenant-123',
      },
    },
  },
];

export function getMockPutSandboxConfigurationResponse(projectName: string, sandboxConfigName: string, request: PutSandboxConfigurationRequest): SandboxConfiguration {
  return {
    id: `/automationProjects/${projectName}/sandboxConfigurations/${sandboxConfigName}`,
    name: sandboxConfigName,
    type: 'automationProjects/sandboxConfigurations',
    properties: {
      provisioningState: SandboxProvisioningState.InProgress,
      ...request.properties,
      createdBy: {
        objectId: 'user-123',
        tenantId: 'tenant-123',
      },
    },
  };
}
